import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { portalQueries } from '@blimo/api-client';
import { useApi, useMe } from '@blimo/api-client/react';
import { formatArea, formatDate, formatMoney, formatMonth, hasPermission, hasRole, labelOf, TENANCY_PARTY_ROLE_LABELS } from '@blimo/shared';
import { FileText, MessageSquare } from 'lucide-react';
import { Button, DescriptionList, ErrorState, Skeleton } from '@blimo/ui';
import { PaymentTimeline } from '@blimo/ui/finance';
import { PageHeader } from '../components/ui/page-header.jsx';
import { Section } from '../components/ui/section.jsx';
import { ContactCard, ManagerCard } from '../components/property-bits.jsx';
import { unitTitle } from '../lib/units.js';

/** Einheit – master data of one of my units. */
export function UnitPage() {
  const api = useApi();
  const { data: me } = useMe();
  const { unitId } = useParams({ strict: false });
  const { data: unit, error, isPending, refetch } = useQuery(portalQueries.unit(api, unitId));
  const property = useQuery({ ...portalQueries.property(api, unit?.property_id ?? 0), enabled: Boolean(unit?.property_id) });

  const back = property.data
    ? { to: '/immobilien/$propertyId', params: { propertyId: String(property.data.id) }, label: property.data.name || 'Liegenschaft' }
    : { to: '/immobilien', label: 'Immobilien' };

  if (error) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title="Einheit" back={back} />
        <ErrorState
          error={error}
          onRetry={() => refetch()}
          notFoundTitle="Einheit nicht gefunden"
          notFoundDescription="Diese Einheit gibt es nicht oder sie ist Ihrem Konto nicht zugeordnet."
          action={
            <Button asChild variant="outline">
              <Link to="/immobilien">Zu meinen Immobilien</Link>
            </Button>
          }
        />
      </div>
    );
  }
  if (isPending) {
    return (
      <div className="flex flex-col gap-4" aria-busy="true" aria-label="Wird geladen">
        <PageHeader title="Einheit" back={back} />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        back={back}
        title={unitTitle(unit)}
        description={property.data?.address?.line}
        actions={
          hasRole(me, 'owner') && (
            <Button asChild variant="outline">
              <Link to="/nachrichten/neu" search={{ unit: String(unit.id) }}>
                <MessageSquare aria-hidden="true" /> Nachricht an Verwaltung
              </Link>
            </Button>
          )
        }
      />
      <div className="grid items-start gap-4 md:grid-cols-2">
        <Section title="Stammdaten">
          <DescriptionList
            labelWidth="8rem"
            items={[
              { label: 'Nummer', value: unit.number },
              { label: 'Bezeichnung', value: unit.designation },
              { label: 'Typ', value: unit.type },
              { label: 'Wohnfläche', value: unit.living_space != null ? <span className="numeric">{formatArea(unit.living_space)}</span> : null },
              { label: 'Ausstattung', value: unit.furnishing },
            ]}
          />
        </Section>
        {property.data && <ContactCard contact={property.data.contact} title="Ansprechpartner" />}
        {property.data?.manager && <ManagerCard manager={property.data.manager} />}
        {hasPermission(me, 'documents.view') && <UnitDocumentsSection unitId={unit.id} />}
        <TenancySection unitId={unit.id} />
        <PaymentsSection unitId={unit.id} />
      </div>
    </div>
  );
}

/**
 * Mietinformationen (GET /portal/units/{id}/tenancy). 404 = no tenancy access for this unit → the section is hidden.
 * Owners with letting access get `tenancy.tenants = null` (no tenant names) – the tenants are shown only when present.
 * @param {{ unitId: number|string }} props
 */
function TenancySection({ unitId }) {
  const api = useApi();
  const query = useQuery(portalQueries.unitTenancy(api, unitId));
  if (query.error?.status === 404) return null;
  if (query.error) {
    return (
      <section aria-label="Mietinformationen" className="md:col-span-2">
        <ErrorState compact error={query.error} onRetry={() => query.refetch()} />
      </section>
    );
  }
  if (query.isPending) return <Skeleton className="h-32 w-full md:col-span-2" />;
  const { tenancy, rent } = query.data ?? {};

  return (
    <Section title="Mietinformationen" description="Aktuelles Mietverhältnis laut Buchhaltung der Verwaltung" className="md:col-span-2" bodyClassName="p-0">
      <div className="grid items-start md:grid-cols-2 md:divide-x">
        <div className="p-4">
          {tenancy ? (
            <DescriptionList
              labelWidth="10rem"
              items={[
                ...(tenancy.tenants !== null && tenancy.tenants !== undefined
                  ? [
                      {
                        label: 'Mieter',
                        value: tenancy.tenants.length ? (
                          <ul className="flex flex-col gap-0.5" aria-label="Mieter">
                            {tenancy.tenants.map((t, i) => (
                              <li key={`${t.role}-${i}`}>
                                {t.name}
                                {t.role !== 'main' && <span className="text-muted-foreground"> ({labelOf(TENANCY_PARTY_ROLE_LABELS, t.role)})</span>}
                              </li>
                            ))}
                          </ul>
                        ) : null,
                      },
                    ]
                  : []),
                { label: 'Konto', value: <span className="numeric">{tenancy.account_number}</span> },
                { label: 'Vertrag vom', value: tenancy.contract_signed_on ? formatDate(tenancy.contract_signed_on) : null },
                { label: 'Mietbeginn', value: tenancy.starts_on ? formatDate(tenancy.starts_on) : null },
                { label: 'Mietende', value: tenancy.ends_on ? formatDate(tenancy.ends_on) : null },
                ...(tenancy.terminated_on ? [{ label: 'Gekündigt am', value: formatDate(tenancy.terminated_on) }] : []),
              ]}
            />
          ) : (
            <p className="py-1 text-[0.8125rem] text-muted-foreground">Derzeit besteht kein aktuelles Mietverhältnis für diese Einheit.</p>
          )}
        </div>
        {rent && (
          <div className="flex flex-col gap-3 border-t p-4 md:border-t-0">
            <h3 className="text-xs font-medium text-muted-foreground">Miete {rent.period ? `(Stand ${formatMonth(rent.period)})` : ''}</h3>
            <div>
              <DescriptionList
                labelWidth="10rem"
                items={[
                  { label: 'Kaltmiete', value: <span className="numeric">{formatMoney(rent.base_rent)}</span> },
                  { label: 'Stellplatz', value: <span className="numeric">{formatMoney(rent.parking_rent)}</span> },
                  { label: 'Betriebskosten', value: <span className="numeric">{formatMoney(rent.operating_costs)}</span> },
                  { label: 'Heizkosten', value: <span className="numeric">{formatMoney(rent.heating_costs)}</span> },
                  { label: 'Gesamt', value: <span className="numeric font-semibold">{formatMoney(rent.total)}</span> },
                ]}
              />
            </div>
          </div>
        )}
      </div>
    </Section>
  );
}

/**
 * Zahlungsverlauf des aktuellen Mietverhältnisses (GET /portal/units/{id}/payment-history). 404 = no access → hidden;
 * `null` = no current tenancy → hidden. All values are computed by the backend.
 * @param {{ unitId: number|string }} props
 */
function PaymentsSection({ unitId }) {
  const api = useApi();
  const query = useQuery(portalQueries.unitPaymentHistory(api, unitId));
  if (query.error?.status === 404 || (query.isSuccess && !query.data)) return null;
  if (query.error) {
    return (
      <section aria-label="Zahlungsverlauf" className="md:col-span-2">
        <ErrorState compact error={query.error} onRetry={() => query.refetch()} />
      </section>
    );
  }
  if (query.isPending) return <Skeleton className="h-28 w-full md:col-span-2" />;

  return (
    <Section title="Zahlungsverlauf" description="Mietzahlungen der letzten 12 Monate laut Buchhaltung der Verwaltung" className="md:col-span-2">
      <PaymentTimeline history={query.data} showRules />
    </Section>
  );
}

/**
 * F-124 (G-19): Dokumentordner der Einheit (GET /portal/documents?unit=) – nur veröffentlichte Dokumente sichtbarer Ordner;
 * ohne Dokumente wird der Abschnitt ausgeblendet.
 * @param {{ unitId: number|string }} props
 */
function UnitDocumentsSection({ unitId }) {
  const api = useApi();
  const query = useQuery(portalQueries.documents(api, { unit: unitId }));
  const folders = (query.data ?? []).filter((f) => (f.documents ?? []).length > 0);
  if (query.isPending || query.error || folders.length === 0) return null;
  return (
    <Section title="Dokumente der Einheit" description="Von Ihrer Verwaltung bereitgestellte Unterlagen" className="md:col-span-2">
      <div className="flex flex-col gap-3">
        {folders.map((folder) => (
          <section key={folder.id} aria-label={folder.name}>
            <h3 className="mb-1 text-xs font-medium text-muted-foreground">{folder.name}</h3>
            <ul className="flex flex-col gap-1">
              {folder.documents.map((d) => (
                <li key={d.id}>
                  <a
                    href={api.portal.documents.downloadUrl(d.id, { inline: true })}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex min-h-7 items-center gap-1.5 text-sm hover:text-(--primary-text) hover:underline"
                  >
                    <FileText className="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
                    {d.title || d.original_name || d.name || `Dokument #${d.id}`}
                  </a>
                </li>
              ))}
            </ul>
          </section>
        ))}
        <Link to="/dokumente" className="text-[0.8125rem] font-medium text-primary hover:underline">
          Alle Dokumente
        </Link>
      </div>
    </Section>
  );
}
